import {
  BaseRepository,
  IBaseModel,
  IListFetchParams,
  MN_ACTIVITIES,
  MN_JOBS,
  MN_ORDERS,
  MN_RESOURCES,
  MODULE,
} from '../common';
import type { ActivityState, Prisma } from '@prisma/client';
import { ActivityStateEnum } from '@jobcost-monorepo/database/prisma/enums';
import * as z from 'zod';
import type { KeyValue } from '@jobcost-monorepo/utils';
import GetTotalWorkedHoursButton from '../ui/CustomComponents/GetTotalWorkedHoursButton';
import HookComponentActivity from '../ui/CustomHookComponent/HookComponentActivity';

type Activity = Prisma.$ActivityPayload['scalars'];

export type IActivity = Activity &
  Omit<IBaseModel, 'createdAt' | 'updatedAt' | 'id'> & {
    job: KeyValue;
    order: KeyValue;
    resource: KeyValue;
  };

export interface IListActivityFetchParams extends IListFetchParams {
  jobId?: number;
  orderId?: number;
  resourceId?: number;
  state?: ActivityState;
  startDate?: string;
  endDate?: string;
}

class ActivityRepository extends BaseRepository<
  IActivity,
  IListActivityFetchParams
> {
  constructor(moduleName: MODULE) {
    super(moduleName);
    this.keyValueMapFields = {
      idlinkedField: 'id',
      descriptionlinkedField: ['resource.name'],
    };
    this.gridFieldsOptions = [
      {
        visible: true,
        position: 0,
        linkedField: 'id',
        type: '',
        align: 'left',
        width: '12rem',
        sortable: false,
        formatType: 'string',
      },
      {
        visible: true,
        position: 1,
        linkedField: 'job.name',
        type: '',
        align: 'left',
        width: 'w-full',
        sortable: false,
        formatType: 'string',
      },
      {
        visible: true,
        position: 2,
        linkedField: 'order.name',
        type: '',
        align: 'left',
        width: 'w-full',
        sortable: false,
        formatType: 'string',
      },
      {
        visible: true,
        position: 3,
        linkedField: 'resource.name',
        type: '',
        align: 'left',
        width: 'w-full',
        sortable: false,
        formatType: 'string',
      },
      {
        visible: true,
        position: 4,
        linkedField: 'startDate',
        type: '',
        align: 'left',
        width: 'w-full',
        sortable: false,
        formatType: 'date',
      },
      {
        visible: true,
        position: 5,
        linkedField: 'endDate',
        type: '',
        align: 'left',
        width: 'w-full',
        sortable: false,
        formatType: 'date',
      },
      {
        visible: true,
        position: 6,
        linkedField: 'workedHours',
        type: '',
        align: 'right',
        width: 'w-full',
        sortable: false,
        formatType: 'number',
      },
      {
        visible: true,
        position: 7,
        linkedField: 'state',
        type: '',
        align: 'left',
        width: 'w-full',
        sortable: false,
        formatType: 'enum',
        translationModuleName: MN_ACTIVITIES,
        enumValues: ActivityStateEnum,
      },
      {
        visible: true,
        position: 8,
        linkedField: 'invoiceNumber',
        type: 'string',
        align: 'left',
        width: 'w-full',
        sortable: false,
        formatType: 'string',
        translationModuleName: MN_ACTIVITIES,
      },
      {
        visible: true,
        position: 9,
        linkedField: 'externalId',
        type: 'number',
        align: 'right',
        width: 'w-full',
        sortable: false,
        formatType: 'string',
        translationModuleName: MN_ACTIVITIES,
      },
    ];

    this.gridFiltersOptions = [
      {
        position: { x: 0, y: 0 },
        linkedField: 'jobId',
        width: 'w-full',
        formatType: 'dropdownWithSearch',
        moduleName: MN_JOBS,
        translationModuleName: MN_JOBS,
      },
      {
        position: { x: 0, y: 1 },
        linkedField: 'orderId',
        width: 'w-full',
        formatType: 'dropdownWithSearch',
        moduleName: MN_ORDERS,
        translationModuleName: MN_ORDERS,
      },
      {
        position: { x: 0, y: 2 },
        linkedField: 'resourceId',
        width: 'w-full',
        formatType: 'dropdownWithSearch',
        moduleName: MN_RESOURCES,
        translationModuleName: MN_RESOURCES,
      },
      {
        position: { x: 0, y: 3 },
        linkedField: 'state',
        width: 'w-full',
        formatType: 'dropdown',
        moduleName: MN_ACTIVITIES,
        translationModuleName: MN_ACTIVITIES,
        enumValues: ActivityStateEnum,
      },
      {
        position: { x: 0, y: 4 },
        linkedField: 'startDate',
        width: 'w-full',
        formatType: 'date',
        moduleName: MN_ACTIVITIES,
        translationModuleName: MN_ACTIVITIES,
      },
      {
        position: { x: 1, y: 4 },
        linkedField: 'endDate',
        width: 'w-full',
        formatType: 'date',
        moduleName: MN_ACTIVITIES,
        translationModuleName: MN_ACTIVITIES,
      },
    ];

    this.formFieldsOptions = [
      {
        visible: false,
        readOnly: true,
        position: { x: 0, y: 0 },
        linkedField: 'id',
        type: 'string',
        width: 'w-full',
        formatType: 'input',
        moduleName: MN_ACTIVITIES,
        translationModuleName: MN_ACTIVITIES,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 0, y: 0 },
        linkedField: 'jobId',
        linkedFieldForDefaultValue: 'job.name',
        type: 'number',
        width: 'w-full',
        formatType: 'dropdownWithSearch',
        moduleName: MN_JOBS,
        translationModuleName: MN_ACTIVITIES,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 1, y: 0 },
        linkedField: 'orderId',
        linkedFieldForDefaultValue: 'order.name',
        type: 'number',
        width: 'w-full',
        formatType: 'dropdownWithSearch',
        moduleName: MN_ORDERS,
        translationModuleName: MN_ACTIVITIES,
        dependsOnFields: ['jobId'],
        dependsOnFieldsForSearch: ['jobId'],
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 0, y: 1 },
        linkedField: 'resourceId',
        linkedFieldForDefaultValue: 'resource.name',
        type: 'number',
        width: 'w-full',
        formatType: 'dropdownWithSearch',
        moduleName: MN_RESOURCES,
        translationModuleName: MN_ACTIVITIES,
        dependsOnFields: ['orderId'],
        dependsOnFieldsForSearch: ['orderId'],
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 1, y: 1 },
        linkedField: 'startDate',
        type: 'date',
        width: 'w-full',
        formatType: 'date',
        moduleName: MN_ACTIVITIES,
        translationModuleName: MN_ACTIVITIES,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 2, y: 1 },
        linkedField: 'endDate',
        type: 'date',
        width: 'w-full',
        formatType: 'date',
        moduleName: MN_ACTIVITIES,
        translationModuleName: MN_ACTIVITIES,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 0, y: 2 },
        linkedField: 'workedHours',
        type: 'number',
        width: 'w-full',
        formatType: 'input',
        moduleName: MN_ACTIVITIES,
        translationModuleName: MN_ACTIVITIES,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 1, y: 2 },
        linkedField: 'getTotalWorkedHoursButton',
        formatType: 'custom',
        customComponent: GetTotalWorkedHoursButton,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 2, y: 2 },
        linkedField: 'state',
        type: 'string',
        width: 'w-full',
        formatType: 'dropdown',
        enumValues: ActivityStateEnum,
        moduleName: MN_ACTIVITIES,
        translationModuleName: MN_ACTIVITIES,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 0, y: 3 },
        linkedField: 'externalId',
        type: 'number',
        width: 'w-full',
        formatType: 'input',
        moduleName: MN_ACTIVITIES,
        translationModuleName: MN_ACTIVITIES,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 1, y: 3 },
        linkedField: 'invoiceNumber',
        type: 'string',
        width: 'w-full',
        formatType: 'input',
        moduleName: MN_ACTIVITIES,
        translationModuleName: MN_ACTIVITIES,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 0, y: 4 },
        linkedField: 'note',
        type: 'string',
        width: '20rem',
        formatType: 'textArea',
        moduleName: MN_ACTIVITIES,
        translationModuleName: MN_ACTIVITIES,
      },
    ];

    // @ts-ignore
    this.formValidationSchema = z.object({
      id: z.number().nullable().optional(),
      jobId: z.number({
        invalid_type_error: 'fieldRequired',
        required_error: 'fieldRequired',
      }),
      orderId: z.number({
        invalid_type_error: 'fieldRequired',
        required_error: 'fieldRequired',
      }),
      resourceId: z.number({
        invalid_type_error: 'fieldRequired',
        required_error: 'fieldRequired',
      }),
      startDate: z.string({
        invalid_type_error: 'fieldRequired',
        required_error: 'fieldRequired',
      }),
      endDate: z.string({
        invalid_type_error: 'fieldRequired',
        required_error: 'fieldRequired',
      }),
      workedHours: z
        .number({
          invalid_type_error: 'fieldRequired',
          required_error: 'fieldRequired',
        })
        .nonnegative({ message: 'nonNegativeNumber' })
        .max(180, { message: 'max_num_180' })
        .nullable()
        .optional(),
      state: z.string({
        invalid_type_error: 'fieldRequired',
        required_error: 'fieldRequired',
      }),
      externalId: z
        .number({
          invalid_type_error: 'fieldRequired',
          required_error: 'fieldRequired',
        })
        .nonnegative({ message: 'nonNegativeNumber' })
        .nullable()
        .optional(),
      invoiceNumber: z
        .string({
          invalid_type_error: 'fieldRequired',
          required_error: 'fieldRequired',
        })
        .max(255, { message: 'max_255' })
        .nullable()
        .optional(),
      note: z
        .string()
        .min(2, { message: 'min_2' })
        .max(255, { message: 'max_255' })
        .nullable()
        .optional(),
    });

    this.hook = HookComponentActivity;
  }
}

export default ActivityRepository;
